import { useState } from 'react';
import {
  Card, CardHeader, CardBody, Button, Modal, Field, Input, Select,
  StatusBadge, ErrorNotice, Loading, ConfirmDialog,
} from '../../components/ui/index.js';
import { useResource, useMutation } from '../../hooks/useApi.js';
import { useToast } from '../../components/ToastProvider.jsx';
import { http } from '../../lib/api.js';
import { formatTimestamp } from '../../lib/format.js';
import { useAuth } from '../../lib/auth.jsx';
import { Plug } from 'lucide-react';
import { PageHero } from '../../components/PageHero.jsx';

/**
 * PP-1: "The connect form is generated from field definitions the provider
 * adapter declares. Adding a provider must require no frontend change."
 *
 * Nothing in this file names a provider or knows what an API Login ID is. The
 * labels, types, help text and which fields are required all arrive from the API.
 */
function ConnectDialog({ provider, open, onClose, onConnected }) {
  const toast = useToast();
  const [environment, setEnvironment] = useState('sandbox');
  const [credentials, setCredentials] = useState({});

  const { mutate, loading, error } = useMutation(async () => {
    await http.post(`/developers/providers/${provider.key}/connect`, { environment, credentials });
    toast.success(`${provider.displayName} connected`, { description: 'We verified the credentials before saving them.' });
    onConnected?.();
    onClose();
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Connect ${provider.displayName}`}
      blurb="We check these against the provider before saving, so you can't end up connected with credentials that can't charge."
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={loading}>Cancel</Button>
          <Button onClick={() => mutate().catch(() => {})} loading={loading}>Verify and connect</Button>
        </>
      }
    >
      <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); mutate().catch(() => {}); }}>
        {/* PP-4: the provider's own reason, not a generic error. */}
        {error && <ErrorNotice error={error} title="Could not connect" />}

        <Field
          label="Environment"
          hint="Test credentials can only take test payments. We refuse a live API key against a test connection."
          required
        >
          {(props) => (
            <Select {...props} value={environment} onChange={(e) => setEnvironment(e.target.value)}>
              {provider.environments.map((env) => (
                <option key={env} value={env}>{env === 'sandbox' ? 'Sandbox (test)' : 'Production (live)'}</option>
              ))}
            </Select>
          )}
        </Field>

        {provider.credentialFields.map((field) => (
          <Field
            key={field.key}
            label={field.label}
            hint={field.help}
            required={field.required}
            error={error?.fieldErrors?.[field.key]}
          >
            {(props) => (
              <Input
                {...props}
                type={field.type === 'password' ? 'password' : 'text'}
                autoComplete="off"
                spellCheck={false}
                value={credentials[field.key] ?? ''}
                onChange={(e) => setCredentials((c) => ({ ...c, [field.key]: e.target.value }))}
              />
            )}
          </Field>
        ))}
      </form>
    </Modal>
  );
}

function ConnectedCard({ provider, connection, onChanged }) {
  const toast = useToast();
  const [disconnecting, setDisconnecting] = useState(false);

  const test = useMutation(async () => {
    const result = await http.post(`/developers/providers/${provider.key}/test`);
    if (result.ok) toast.success('Connection verified');
    else toast.error(result.message ?? 'Verification failed');
    onChanged?.();
  });

  const makeDefault = useMutation(async () => {
    await http.post(`/developers/providers/${provider.key}/default`);
    toast.success(`${provider.displayName} now takes your card payments`, {
      description: 'Existing payments are unaffected — this changes where new ones go.',
    });
    onChanged?.();
  });

  const disconnect = useMutation(async () => {
    await http.delete(`/developers/providers/${provider.key}`);
    toast.success(`${provider.displayName} disconnected`, { description: 'The stored credentials were deleted.' });
    setDisconnecting(false);
    onChanged?.();
  });

  return (
    <>
      <Card>
        <CardHeader
          title={provider.displayName}
          blurb={`Connected ${formatTimestamp(connection.connectedAt)}`}
          actions={
            <>
              {/*
                * Which one actually takes payments. It used to be whichever was
                * connected most recently — invisible, and occasionally a gateway
                * a merchant had only been experimenting with.
                */}
              {connection.isDefault && <span className="chip-success">Takes payments</span>}
              <StatusBadge status={connection.environment} size="sm" />
              <StatusBadge status={connection.status} size="sm" />
            </>
          }
        />
        <CardBody className="space-y-4">
          {connection.status === 'error' && connection.lastVerificationError && (
            <ErrorNotice
              error={{ message: connection.lastVerificationError }}
              title="This connection failed its last check"
              onRetry={() => test.mutate().catch(() => {})}
            />
          )}

          {/* PP-5: only a masked hint is ever shown — to anyone, including us. */}
          <div className="grid gap-3 sm:grid-cols-2">
            {Object.entries(connection.credentialHints ?? {})
              .filter(([, value]) => value)
              .map(([key, value]) => (
                <div key={key}>
                  <p className="text-xs font-medium uppercase tracking-wide text-muted">
                    {key.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase())}
                  </p>
                  <p className="mt-0.5 font-mono text-sm text-ink-soft">{value}</p>
                </div>
              ))}
          </div>

          <p className="text-xs text-muted">
            Credentials are encrypted at rest and never returned to any browser — including this one.
            {connection.lastVerifiedAt && ` Last checked ${formatTimestamp(connection.lastVerifiedAt)}.`}
          </p>

          <div className="flex flex-wrap items-center gap-2 border-t border-line pt-4">
            <Button variant="secondary" loading={test.loading} onClick={() => test.mutate().catch(() => {})}>
              Test connection
            </Button>

            {!connection.isDefault && (
              <Button
                variant="secondary"
                loading={makeDefault.loading}
                onClick={() => makeDefault.mutate().catch(() => {})}
              >
                Use this one
              </Button>
            )}

            <Button variant="ghost" onClick={() => setDisconnecting(true)}>Disconnect</Button>

            {connection.isDefault && (
              <span className="ml-auto text-xs text-muted">
                Card payments go through {provider.displayName}.
              </span>
            )}
          </div>
        </CardBody>
      </Card>

      {/* FE-10: confirms first, and states the consequence in plain words. */}
      <ConfirmDialog
        open={disconnecting}
        onClose={() => setDisconnecting(false)}
        onConfirm={() => disconnect.mutate().catch(() => {})}
        loading={disconnect.loading}
        title={`Disconnect ${provider.displayName}?`}
        consequence={
          `Your stored credentials will be deleted, not just disabled. You will not be able to take new payments ` +
          `until you connect a provider again, and you will need to re-enter your keys to reconnect. ` +
          `Existing payments and refunds are unaffected.`
        }
        confirmLabel="Disconnect"
      />
    </>
  );
}


/**
 * What the customer will actually experience. This is the difference merchants
 * care about and the one most provider comparisons bury.
 */
const FLOW_COPY = {
  direct: 'Card details are entered on your own checkout page. Your customer never sees another brand.',
  embedded: 'The payment window opens on your own checkout page. Your customer stays with you.',
  hosted_redirect: 'Your customer is sent to the provider\u2019s page to pay, then returns.',
};

const flowDescription = (capabilities) =>
  FLOW_COPY[capabilities?.flow] ?? 'Not connected — you cannot take payments until a provider is connected.';

const FLOW_LABEL = { direct: 'On your page', embedded: 'On your page', hosted_redirect: 'Redirect' };

function FlowChip({ capabilities }) {
  if (!capabilities?.flow) return null;
  const isRedirect = capabilities.flow === 'hosted_redirect';
  return (
    <span className={isRedirect ? 'chip-warning' : 'chip-success'}>
      {FLOW_LABEL[capabilities.flow]}
    </span>
  );
}

/**
 * An adapter written from a provider's documentation has not been proven against
 * their live sandbox. Saying so is the honest thing: "the code exists" and "this
 * will take money correctly" are different claims.
 */
function CertificationChip({ certification }) {
  if (certification !== 'docs_only') return null;
  return (
    <span className="chip-warning" title="Written from the provider's documentation, not yet proven against their live sandbox">
      Needs certification
    </span>
  );
}

export function ProvidersPage() {
  const { refreshMerchant } = useAuth();
  const { data, error, loading, refetch } = useResource('/developers/providers');
  const [connecting, setConnecting] = useState(null);

  const onChanged = () => { refetch(); refreshMerchant(); };

  if (loading) return <Loading label="Loading providers…" />;
  if (error) {
    return (
      <>
        <PageHero title="Payment providers" />
        <ErrorNotice error={error} onRetry={refetch} />
      </>
    );
  }

  const available = data.filter((p) => p.status === 'available');
  const comingSoon = data.filter((p) => p.status !== 'available');

  return (
    <>
      <PageHero
        icon={Plug}
        chip="Developers"
        title="Payment providers"
        blurb="Connect the payment account you already have. Your customers never see it."
      />

      {/* PP-10: merchants will otherwise ask. */}
      <Card className="mb-6 border-accent-200 bg-accent-soft">
        <CardBody>
          <h2 className="text-sm font-semibold text-accent-ink">Why we ask for a public client key</h2>
          <p className="mt-1.5 max-w-3xl text-sm leading-relaxed text-ink-soft">
            Your checkout page uses it to send card details from your customer&rsquo;s browser straight to the
            payment network, signed with your own account. The card number never passes through our servers,
            which is what keeps card data out of our systems — and keeps your compliance burden small.
          </p>
        </CardBody>
      </Card>

      <div className="space-y-4">
        {available.map((provider) =>
          provider.connection ? (
            <ConnectedCard key={provider.key} provider={provider} connection={provider.connection} onChanged={onChanged} />
          ) : (
            <Card key={provider.key}>
              <CardBody className="flex flex-wrap items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-sm font-semibold text-ink">{provider.displayName}</h2>
                    <FlowChip capabilities={provider.capabilities} />
                    <CertificationChip certification={provider.certification} />
                  </div>
                  <p className="mt-1 text-sm text-muted">{flowDescription(provider.capabilities)}</p>
                </div>
                <Button onClick={() => setConnecting(provider)}>Connect</Button>
              </CardBody>
            </Card>
          ),
        )}
      </div>

      {/* PP-9: the whole catalogue, so a merchant can see where the platform is going. */}
      {comingSoon.length > 0 && (
        <div className="mt-8">
          <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted">Coming soon</h2>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {comingSoon.map((provider) => (
              <div key={provider.key} className="rounded-xl border border-dashed border-line bg-white/60 p-4">
                <p className="text-sm font-medium text-ink-soft">{provider.displayName}</p>
                <p className="mt-0.5 text-xs text-faint">
                  Your integration will not change when we add it.
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {connecting && (
        <ConnectDialog
          provider={connecting}
          open={Boolean(connecting)}
          onClose={() => setConnecting(null)}
          onConnected={onChanged}
        />
      )}
    </>
  );
}

export default ProvidersPage;
